import { FormEvent, useState, FC } from 'react'
import { useRouter } from 'next/router'

import { BidStateType } from 'hooks/auction'
import { AuctionByArtworkQuery, UserOffersQuery } from 'gql'
import Placeholder from '../common/placeholder'
import PlaceBidComponent from './component'
import getUserBiddingStatusOnArtwork from '../bid-helpers'
import { CONFIRM_BID, JOIN } from '../../../routes/paths'
import { formatCurrency } from '../../../utils'

interface PlaceBidProps {
  userData: UserOffersQuery
  auction: AuctionByArtworkQuery['auction']
  pusherData: BidStateType
  loading?: boolean
}

const PlaceBid: FC<PlaceBidProps> = ({ userData, auction, loading, pusherData }) => {
  const [error, setError] = useState('')
  const [bid, setBid] = useState(0)

  const { push } = useRouter()

  const endDate = auction?.endDate
  const startDate = auction?.startDate
  const purchaseStatus = auction?.purchaseStatus
  const startingBid = pusherData.bid || auction?.startingBid
  const currentBid = pusherData.bid || auction?.currentBid
  const nextBid = pusherData.nextBid || auction?.nextBid

  const { userHasMaxBid, isUserHighestBidder, userMaxBidAmount, isEnd, willStart } =
    getUserBiddingStatusOnArtwork({
      purchaseStatus,
      currentBid,
      allOffers: userData?.currentUser?.offers?.edges,
      start: startDate,
      end: endDate,
    })

  const placeholder = `Enter ${
    nextBid ? formatCurrency(nextBid || 0, 0) : formatCurrency(startingBid || 0, 0)
  } or more`

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError('')

    if (!bid) {
      setError('Please, enter your bid.')
      return
    }

    if (nextBid > bid || startingBid > bid) {
      setError(`${placeholder}.`)
      return
    }

    if (userHasMaxBid && bid < userMaxBidAmount) {
      setError('Sorry, you cannot lower your max bid.')
      return
    }

    if (!userData.currentUser) {
      push({ query: { auction: auction.pk, bid }, pathname: JOIN })
      return
    }

    push({ query: { auction: auction.pk, bid }, pathname: CONFIRM_BID })
  }

  if (loading) {
    return <Placeholder />
  }

  if (isEnd || willStart) return null

  return (
    <PlaceBidComponent
      isHighestBidder={isUserHighestBidder}
      userMaxBidAmount={userMaxBidAmount}
      userHasMaxBid={userHasMaxBid && currentBid < userMaxBidAmount}
      placeholder={placeholder}
      error={error}
      bid={bid}
      onSubmit={onSubmit}
      setBid={setBid}
    />
  )
}

export default PlaceBid
